import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { usePageHeaderStore } from '../../stores/pageHeader';
import {
  Layers,
  FileText,
  ChevronRight,
  User,
  Plus,
  Edit3,
  X,
  AlertCircle,
  Calendar,
  CheckCircle2,
  LayoutGrid
} from 'lucide-react';
import { useEffect, useState } from 'react';

interface EpicDetail {
  id: string;
  projectId: string;
  title: string;
  description: string | null;
  status: string;
  isSystem: boolean;
  sortOrder: number;
  requestCount: number;
  createdAt: string;
  updatedAt: string;
}

interface Story {
  id: string;
  title: string;
  status: string;
  priority: string | null;
  assignee: { id: string; email: string; fullName: string | null } | null;
  userStory: { asA: string | null; iWant: string | null; soThat: string | null } | null;
  acceptanceCriteriaCount: number;
  selectedElementsCount: number;
  createdAt: string;
  updatedAt: string;
}

interface ProjectInfo {
  id: string;
  name: string;
  key: string;
  orgId: string;
  orgName: string;
}

// ============================================
// New Story Modal
// ============================================

function NewStoryModal({
  projectId,
  epicId,
  onClose,
  onSuccess,
}: {
  projectId: string;
  epicId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [title, setTitle] = useState('');
  const [asA, setAsA] = useState('');
  const [iWant, setIWant] = useState('');
  const [soThat, setSoThat] = useState('');
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: async () => {
      return api.post('/requests', {
        projectId,
        epicId,
        title: title || `Story: ${iWant.substring(0, 50)}...`,
        userStory: {
          asA: asA || null,
          iWant: iWant || null,
          soThat: soThat || null,
        },
        status: 'DRAFT',
      });
    },
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to create story');
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg animate-fade-in">
        <div className="card-header justify-between">
          <h3 className="card-title flex items-center gap-2">
            <Plus className="w-5 h-5" style={{ color: 'var(--primary)' }} />
            New User Story
          </h3>
          <button onClick={onClose} className="btn btn-ghost p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Story Title <span className="text-muted font-normal">(optional)</span>
            </label>
            <input
              type="text"
              className="input w-full"
              placeholder="Auto-generated from user story if empty"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          
          <div className="p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg border border-neutral-200 dark:border-neutral-700 space-y-3">
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">User Story</p>
            
            <div>
              <label className="block text-sm text-neutral-600 dark:text-neutral-300 mb-1">
                As a <span className="text-neutral-400 dark:text-neutral-500">(user role)</span>
              </label>
              <input
                type="text"
                className="input w-full"
                placeholder="e.g., registered customer"
                value={asA}
                onChange={(e) => setAsA(e.target.value)}
              />
            </div>
            
            <div>
              <label className="block text-sm text-neutral-600 dark:text-neutral-300 mb-1">
                I want <span className="text-neutral-400 dark:text-neutral-500">(action/feature)</span>
              </label>
              <textarea
                className="input w-full resize-none"
                rows={2}
                placeholder="e.g., to view my order history"
                value={iWant}
                onChange={(e) => setIWant(e.target.value)}
              />
            </div>
            
            <div>
              <label className="block text-sm text-neutral-600 dark:text-neutral-300 mb-1">
                So that <span className="text-neutral-400 dark:text-neutral-500">(benefit/goal)</span>
              </label>
              <textarea
                className="input w-full resize-none"
                rows={2}
                placeholder="e.g., I can track my purchases"
                value={soThat}
                onChange={(e) => setSoThat(e.target.value)}
              />
            </div>
          </div>
          
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-danger/10 text-danger">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || (!title.trim() && !iWant.trim())}
            className="btn btn-primary"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Story'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Edit Epic Modal
// ============================================

function EditEpicModal({
  epic,
  onClose,
  onSuccess,
}: {
  epic: EpicDetail;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [title, setTitle] = useState(epic.title);
  const [description, setDescription] = useState(epic.description || '');
  const [status, setStatus] = useState(epic.status);
  const [error, setError] = useState('');

  const updateMutation = useMutation({
    mutationFn: async () => {
      return api.patch(`/epics/${epic.id}`, {
        title,
        description: description || null,
        status,
      });
    },
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to update epic');
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg animate-fade-in">
        <div className="card-header justify-between">
          <h3 className="card-title flex items-center gap-2">
            <Edit3 className="w-5 h-5" style={{ color: 'var(--primary)' }} />
            Edit Epic
          </h3>
          <button onClick={onClose} className="btn btn-ghost p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Epic Title
            </label>
            <input
              type="text"
              className="input w-full"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Status
            </label>
            <select
              className="input w-full"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="NOT_STARTED">Not Started</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="ON_HOLD">On Hold</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Description <span className="text-muted font-normal">(optional)</span>
            </label>
            <textarea
              className="input w-full resize-none"
              rows={3}
              placeholder="Brief description of the epic..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-danger/10 text-danger">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending || !title.trim()}
            className="btn btn-primary"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function EpicDetailPage() {
  const { orgId, projectId, epicId } = useParams<{
    orgId: string;
    projectId: string;
    epicId: string;
  }>();
  const { setHeader, clearHeader } = usePageHeaderStore();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewStoryModal, setShowNewStoryModal] = useState(false);
  const queryClient = useQueryClient();
  
  const { data: _project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<ProjectInfo>(`/projects/${projectId}`),
    enabled: !!projectId,
  });
  
  const { data: epic, isLoading } = useQuery({
    queryKey: ['epic', epicId],
    queryFn: () => api.get<EpicDetail>(`/epics/${epicId}`),
    enabled: !!epicId,
  });
  
  const { data: stories } = useQuery({
    queryKey: ['epic-stories', epicId, projectId],
    queryFn: () => api.get<{ data: Story[] }>(`/requests?projectId=${projectId}&epicId=${epicId}`),
    enabled: !!epicId && !!projectId,
  });
  
  const updateEpicMutation = useMutation({
    mutationFn: (data: { title?: string }) =>
      api.patch(`/epics/${epicId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epic', epicId] });
    },
  });

  // Set header when epic data is loaded (must be before any early returns!)
  useEffect(() => {
    if (epic) {
      setHeader(
        epic.title,
        epic.description || undefined,
        <Layers className="w-5 h-5" style={{ color: 'var(--primary)' }} />,
        <span className={`badge status-${epic.status.toLowerCase().replace('_', '-')}`}>
          {epic.status.replace('_', ' ')}
        </span>,
        {
          isEditable: true,
          onTitleChange: (newTitle) => {
            updateEpicMutation.mutate({ title: newTitle });
          },
          actions: [
            {
              id: 'edit-epic',
              label: 'Edit Epic',
              icon: <Edit3 className="w-4 h-4" />,
              variant: 'secondary',
              onClick: () => setShowEditModal(true),
            },
            {
              id: 'new-story',
              label: 'New Story',
              icon: <Plus className="w-4 h-4" />,
              variant: 'primary',
              onClick: () => setShowNewStoryModal(true),
            },
          ],
        }
      );
    }
    return () => clearHeader();
  }, [epic, setHeader, clearHeader]);
  
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-surface-200 rounded w-1/4" />
        <div className="h-48 bg-surface-200 rounded" />
      </div>
    );
  }
  
  if (!epic) {
    return (
      <div className="text-center py-12">
        <p className="text-surface-500">Epic not found</p>
        <Link 
          to={`/app/organizations/${orgId}/projects/${projectId}`} 
          className="btn btn-secondary mt-4"
        >
          Back to Project
        </Link>
      </div>
    );
  }
  
  return (
    <div className="animate-fade-in">
      {/* Stories */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Stories</h3>
        </div>
        <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
          {stories?.data.map((story) => (
            <Link
              key={story.id}
              to={`/app/organizations/${orgId}/projects/${projectId}/epics/${epicId}/stories/${story.id}`}
              className="flex items-center justify-between py-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 -mx-6 px-6 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-neutral-400" />
                <div>
                  <p className="font-medium text-neutral-900 dark:text-neutral-100 group-hover:text-primary transition-colors">{story.title}</p>
                  {story.userStory?.iWant && (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 line-clamp-1 max-w-lg">
                      <span className="text-neutral-400 dark:text-neutral-500">I want</span> {story.userStory.iWant}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                    {story.assignee && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {story.assignee.fullName || story.assignee.email.split('@')[0]}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <LayoutGrid className="w-3 h-3" />
                      {story.selectedElementsCount ?? 0} elements
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      {story.acceptanceCriteriaCount ?? 0} AC
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(story.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {story.priority && (
                  <span className={`badge priority-${story.priority.toLowerCase()}`}>
                    {story.priority}
                  </span>
                )}
                <span className={`badge status-${story.status.toLowerCase().replace('_', '-')}`}>
                  {story.status.replace('_', ' ')}
                </span>
                <ChevronRight className="w-4 h-4 text-neutral-400 dark:text-neutral-500 group-hover:text-primary transition-all" />
              </div>
            </Link>
          ))}
          {(!stories?.data || stories.data.length === 0) && (
            <div className="text-center py-8">
              <FileText className="w-10 h-10 text-neutral-300 dark:text-neutral-600 mx-auto mb-3" />
              <p className="text-neutral-500 dark:text-neutral-400">No stories yet</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Edit Epic Modal */}
      {showEditModal && epic && (
        <EditEpicModal
          epic={epic}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['epic', epicId] });
          }}
        />
      )}
      
      {/* New Story Modal */}
      {showNewStoryModal && projectId && epicId && (
        <NewStoryModal
          projectId={projectId}
          epicId={epicId}
          onClose={() => setShowNewStoryModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['epic-stories', epicId, projectId] });
            queryClient.invalidateQueries({ queryKey: ['epic', epicId] });
          }}
        />
      )}
    </div>
  );
}

